import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useDailyProgress } from 'utils/userContext.js';
import ROUTES from 'utils/routes.js';

import Confetti from 'react-confetti'
import { useAudio } from 'utils/audioContext';

function Avatar({ showRings = true, summaryAvatar = false, size = "sm", dropdown = true, exampleUser = null, exampleProgress = null, hoverAnims = false, ...rest }) {
    dropdown = false; // Disable dropdown for now

    const [timeGoal, setTimeGoal] = useState(false);
    const { play } = useAudio();

    useEffect(() => {
        if (summaryAvatar) return;
        window.finishTimeGoal = () => {
            if (timeGoal) return;
            setTimeGoal(true);
            play("complete.mp3")
            console.log("Time goal complete!");

            setTimeout(() => {
                setTimeGoal(false);
            }, 5000);
        }
        return () => {
            window.finishTimeGoal = null;
        }
    }, []);


    const realUser = useUser();
    const realProgress = useDailyProgress();

    const { user, loading, logout } = exampleUser ? { user: exampleUser, loading: false, logout: () => { } } : realUser;
    const { loading: progressLoading, lessonsComplete = 0, minutesComplete = 0 } = exampleProgress ? { loading: false, ...exampleProgress } : realProgress;

    // Calculate the number of filled, and total segments based on minutes complete, and goal
    const segments = 20;
    const defFilled = Math.round((minutesComplete || 0) / (user?.timeGoal || 30) * segments);


    const [filled, setFilled] = React.useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const avatarRef = useRef();
    const avatarPopupRef = useRef();

    const sizes = {
        xs: { width: 13, height: 13, padding: -8, size: 9, borderRadius: 7 },
        sm: { width: 18, height: 18, padding: -8, size: 9, borderRadius: 7 },
        md: { width: 19, height: 19, padding: -7, size: 9, borderRadius: 7 },
        lg: { width: 40, height: 40, padding: 0, size: 22, borderRadius: 7 },
        xl: { width: 56, height: 56, padding: 16, size: 33, borderRadius: 7 },
        // xl: { width: 37, height: 37, padding: 10, size: 22 },
        xxl: { width: 65, height: 65, padding: 18, size: 37, borderRadius: 12 },
    }
    let width = sizes[size].width;
    let height = sizes[size].height;


    const radius = 2 * height;
    const squareBorderRadius = sizes[size].borderRadius;
    const squareSize = sizes[size].size;
    const radPadding = sizes[size].padding;
    const dropShadowPadding = 8;
    const extraSvgPadding = 20;
    const maxDistance = radius + radPadding + squareSize / 2 + dropShadowPadding + extraSvgPadding;
    const svgSize = maxDistance * 2;
    const center = maxDistance;
    const circumference = 2 * Math.PI * radius;
    const totalSquaresWidth = segments * squareSize;
    const gap = (circumference - totalSquaresWidth) / segments;




    useEffect(() => {
        const target = defFilled;
        let timeoutId;
        let current = 0;
        function fillNext() {
            setFilled((prev) => {
                current = prev + 1;
                return current;
            });
            if (current < target - 1) {
                const delay = 40 + 100 * (current / target) ** 5;
                timeoutId = setTimeout(fillNext, delay);
            }
        }
        setFilled(0);
        if (target > 0) fillNext();
        return () => clearTimeout(timeoutId);
    }, [segments, defFilled]);

    // Helper to get position for each square
    function getSquareProps(index) {
        const anglePerSegment = 360 / segments;
        const angle = index * anglePerSegment;
        const distance = radius + radPadding;
        const x = center - squareSize / 2;
        const y = center - distance - squareSize / 2;
        return { x, y, transform: `rotate(${angle} ${center} ${center})` };
    }

    useEffect(() => {
        if (!dropdown || !showPopup) return;
        function handleClick(e) {
            if (avatarRef.current && !avatarRef.current.contains(e.target)) {
                setShowPopup(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [dropdown, showPopup]);

    const handleAvatarClick = (e) => {
        if (!dropdown) return;
        e.preventDefault();
        if (avatarPopupRef.current.contains(e.target)) return; // Prevent closing if clicking inside the popup
        setShowPopup((v) => {
            return !v;
        });
    };




    // Render avatar content (SVG, image, or custom text)
    function renderAvatarContent() {
        return (
            <>
                {showRings && <svg
                    width={svgSize}
                    height={svgSize}
                    className="absolute left-1/2 top-1/2 "
                    style={{ transform: 'translate(-50%, -50%)', zIndex: 1 }}
                >
                    <defs>
                        <radialGradient id="avatarGlowGradient" cx="50%" cy="50%" r="70%">
                            <stop offset="0%" stopColor="#d9b8ff" />
                            <stop offset="100%" stopColor="var(--primary)" />
                        </radialGradient>
                        <radialGradient id="avatarRingGradient" cx="50%" cy="50%" r="70%">
                            <stop offset="0%" stopColor="#4a2f63" />
                            <stop offset="100%" stopColor="#3f2854" />
                        </radialGradient>
                    </defs>
                    {Array.from({ length: segments }).map((_, i) => {
                        const { x, y, transform } = getSquareProps(i);
                        return (
                            <g key={i} className={hoverAnims ? 'avatar-square-hover' : ''}>
                                <rect
                                    x={x}
                                    y={y}
                                    width={squareSize}
                                    height={squareSize}
                                    transform={transform}
                                    rx={squareBorderRadius}
                                    ry={squareBorderRadius}
                                    style={{ filter: 'drop-shadow(0 0 1px var(--primary-dark))', fill: 'url(#avatarRingGradient)', transition: 'filter 0.3s ease-out' }}
                                />
                                <rect
                                    x={x}
                                    y={y}
                                    width={squareSize}
                                    height={squareSize}
                                    transform={transform}
                                    rx={squareBorderRadius}
                                    ry={squareBorderRadius}
                                    style={{ filter: 'drop-shadow(0 0 5px var(--primary))', fill: 'url(#avatarGlowGradient)', opacity: filled > i ? 1 : 0, transition: 'opacity 0.7s cubic-bezier(.4,2,.6,1), filter 0.3s ease-out' }}

                                />
                            </g>
                        );
                    })}
                </svg>}
                {!summaryAvatar ? (
                    <div className="relative w-full h-full rounded-full overflow-hidden avatar-inner-shadow pointer-events-none">
                        <div
                            className="relative rounded-full bg-background-light bg-center bg-cover _noDrag"
                            style={{ width: '100%', height: '100%', backgroundImage: user?.avatar ? `url(${encodeURI(user.avatar)})` : 'url("")' }}
                        />
                        <style>
                            {`
                            .avatar-inner-shadow::after {
                                content: '';
                                position: absolute;
                                top: 0;
                                left: 0;
                                width: 100%;
                                height: 100%;
                                background: radial-gradient(circle, transparent 45%, var(--primary-dark) 140%);
                                border-radius: 50%;
                                z-index: 15;
                                pointer-events: none;
                            }
                            `}
                        </style>
                    </div>
                ) : (
                    <div className="flex items-center text-center text-accent font-mono font-black [filter:drop-shadow(0_0_2px_var(--accent))]" style={{ fontSize: String(lessonsComplete || 0).length > 1 ? '4rem' : '5rem' }}>{String(lessonsComplete || 0)}</div>
                )}

            </>
        );
    }

    const navigate = useNavigate();

    const logoutButton = async () => {
        await logout();
        navigate(ROUTES.LOGIN);
    };

    return (
        <div
            ref={avatarRef}
            className={'aspect-square _noDrag select-none rounded-full relative flex items-center justify-center ' + (dropdown ? 'cursor-pointer' : '')}
            style={{ padding: showRings ? "1rem" : "", margin: showRings ? (radPadding + squareSize) / 2 : 0, width: `${width / 4}rem`, height: `${height / 4}rem` }}
            onClick={handleAvatarClick}
            {...rest}
        >
            {renderAvatarContent()}
            {timeGoal && <div>
                <h1 className='absolute top-[50%] right-full text-right w-max text-lg font-medium -translate-y-1/2 mr-3 timeCompleteText'>Time goal complete!</h1>

                <div className='absolute right-[50%] top-[80%] w-min h-min' style={{
                    transformOrigin: 'top right',
                    transform: 'scale(0.5) translate(50%, 0)',
                }}>
                    <Confetti
                        width={750}
                        height={Math.min(1300, window.innerHeight * 2 - 200)}
                        recycle={false}
                        numberOfPieces={1000}
                        colors={['#8227d6', '#6f19bf', '#4e0a8c']}
                        initialVelocityY={{ min: 0, max: 3 }}
                        initialVelocityX={{ min: -5, max: 5 }}
                        tweenDuration={1000}
                        gravity={0.1}
                        confettiSource={{ x: 750 / 2 - 50, y: 10, w: 100, h: 10 }}
                        opacity={0.5}
                        style={{
                            position: 'static',
                            inset: 0,
                            // border: "2px solid red",
                        }}
                    />
                </div>
            </div>}
            {dropdown && (
                <div
                    ref={avatarPopupRef}
                    className={`absolute right-0 top-full mt-2 z-50 bg-background-light bg-opacity-90 rounded-3xl shadow-xl p-6 py-5 min-w-[250px] transition-all duration-200 ease cursor-auto select-text _noDrag text-left`}
                    style={{
                        opacity: showPopup ? 1 : 0,
                        visibility: showPopup ? 'visible' : 'hidden',
                        translate: "0 " + (showPopup ? '0px' : '-5px'),
                        pointerEvents: showPopup ? 'auto' : 'none'
                    }}
                >
                    <div className='flex flex-col gap-1 text-text text-sm '>
                        <p className='font-bold text-xl -mb-1'>{loading ? "Name" : user.username}</p>
                        <p className='text-xs text-border-light'>{loading ? "Email" : user.email}</p>
                        {/* <div className='w-full border-border border-t-2 my-2'></div>
                        <div className='flex flex-col gap-3 py-1'>
                            <Link to={ROUTES.SUMMARY} className='text-base font-medium flex items-center gap-[9px]'><div className='w-5'>{SummaryIcon()}</div>Summary</Link>
                            <Link to={ROUTES.SUMMARY} className='text-base font-medium flex items-center gap-[9px]'><div className='w-5 pt-[2px]'>{SettingsIcon()}</div>Settings</Link>
                            <Link to={ROUTES.SUMMARY} className='text-base font-medium flex items-center gap-[9px]'><div className='w-5 pt-[3px]'>{SubscriptionIcon()}</div>Subscription</Link>
                        </div> */}
                        <div className='w-full border-border border-t-2 my-3'></div>
                        <div className='text-base font-medium flex items-center gap-[9px] cursor-pointer' onClick={logoutButton}><div className='w-5'>{SignOutIcon()}</div>Sign out</div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const SummaryIcon = () => (
    <svg viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.2405 11.707C9.96567 10.8763 9.35871 10.1426 8.515 9.61924C7.6713 9.09584 6.63735 8.81201 5.5738 8.81201C4.51026 8.81201 3.47631 9.09584 2.6326 9.61924C1.78889 10.1426 1.18193 10.8763 0.907135 11.707" stroke="var(--text)" strokeLinecap="round" />
        <path d="M5.57379 6.20791C6.90799 6.20791 7.98956 5.04208 7.98956 3.60395C7.98956 2.16583 6.90799 1 5.57379 1C4.2396 1 3.15802 2.16583 3.15802 3.60395C3.15802 5.04208 4.2396 6.20791 5.57379 6.20791Z" stroke="var(--text)" strokeLinecap="round" />
    </svg>
);

export const SettingsIcon = () => (
    <svg viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M5.93744 4.81299C5.35728 4.81299 4.80088 5.04346 4.39064 5.45369C3.98041 5.86393 3.74994 6.42033 3.74994 7.00049C3.74994 7.58065 3.98041 8.13705 4.39064 8.54728C4.80088 8.95752 5.35728 9.18799 5.93744 9.18799C6.5176 9.18799 7.074 8.95752 7.48423 8.54728C7.89447 8.13705 8.12494 7.58065 8.12494 7.00049C8.12494 6.42033 7.89447 5.86393 7.48423 5.45369C7.074 5.04346 6.5176 4.81299 5.93744 4.81299ZM4.62494 7.00049C4.62494 6.65239 4.76322 6.31855 5.00936 6.07241C5.2555 5.82627 5.58934 5.68799 5.93744 5.68799C6.28554 5.68799 6.61937 5.82627 6.86552 6.07241C7.11166 6.31855 7.24994 6.65239 7.24994 7.00049C7.24994 7.34858 7.11166 7.68242 6.86552 7.92857C6.61937 8.17471 6.28554 8.31299 5.93744 8.31299C5.58934 8.31299 5.2555 8.17471 5.00936 7.92857C4.76322 7.68242 4.62494 7.34858 4.62494 7.00049Z" fill="var(--text)" />
        <path fillRule="evenodd" clipRule="evenodd" d="M5.92287 0.729492C5.66328 0.729492 5.44687 0.729492 5.26953 0.741159C5.08824 0.74846 4.90943 0.785917 4.74045 0.851992C4.54576 0.932544 4.36885 1.05066 4.21982 1.19959C4.07078 1.34853 3.95255 1.52536 3.87187 1.71999C3.78728 1.92416 3.76453 2.13999 3.7552 2.37449C3.75416 2.46023 3.73141 2.54431 3.68908 2.61888C3.64674 2.69345 3.58621 2.75607 3.51312 2.80091C3.43776 2.84193 3.35324 2.86318 3.26745 2.86267C3.18166 2.86216 3.09739 2.83991 3.02253 2.79799C2.81487 2.68833 2.61653 2.60024 2.3972 2.57108C2.18833 2.5436 1.9761 2.55754 1.77262 2.61209C1.56914 2.66663 1.3784 2.76073 1.21128 2.88899C1.06976 3.00244 0.948126 3.13866 0.851367 3.29208C0.7522 3.44024 0.6437 3.62749 0.5142 3.85208L0.499617 3.87774C0.369533 4.10233 0.261617 4.28958 0.18345 4.44941C0.101783 4.61624 0.0376167 4.78074 0.0142833 4.96274C-0.0413665 5.38449 0.0727798 5.81107 0.331617 6.14866C0.466367 6.32424 0.64195 6.45199 0.840283 6.57683C0.914153 6.62063 0.975699 6.68245 1.01916 6.75652C1.06262 6.83059 1.08658 6.91448 1.08878 7.00033C1.08658 7.08618 1.06262 7.17006 1.01916 7.24413C0.975699 7.3182 0.914153 7.38003 0.840283 7.42383C0.64195 7.54866 0.46695 7.67641 0.331617 7.85199C0.203351 8.01911 0.109258 8.20985 0.0547101 8.41333C0.000161838 8.61681 -0.0137734 8.82904 0.0137001 9.03791C0.0376167 9.21991 0.1012 9.38441 0.182867 9.55124C0.261617 9.71108 0.369533 9.89833 0.499617 10.1229L0.5142 10.1486C0.6437 10.3732 0.7522 10.5604 0.851367 10.7086C0.954617 10.862 1.06545 11.0002 1.21128 11.1111C1.37836 11.2394 1.56908 11.3336 1.77256 11.3883C1.97604 11.4429 2.1883 11.457 2.3972 11.4296C2.61653 11.4004 2.81487 11.3129 3.02253 11.2027C3.09732 11.1608 3.18148 11.1387 3.26716 11.1381C3.35284 11.1376 3.43725 11.1588 3.51253 11.1997C3.58622 11.244 3.64729 11.3065 3.68988 11.3812C3.73248 11.4558 3.75517 11.5402 3.75578 11.6262C3.76453 11.8607 3.78728 12.0765 3.87245 12.2807C3.953 12.4753 4.07112 12.6523 4.22005 12.8013C4.36898 12.9503 4.54582 13.0686 4.74045 13.1492C4.90962 13.2192 5.08462 13.2467 5.26953 13.2589C5.44687 13.2712 5.66328 13.2712 5.92287 13.2712H5.95203C6.21162 13.2712 6.42803 13.2712 6.60537 13.2595C6.79087 13.2467 6.96528 13.2192 7.13445 13.1487C7.32914 13.0681 7.50605 12.95 7.65508 12.8011C7.80412 12.6521 7.92235 12.4753 8.00303 12.2807C8.08762 12.0765 8.11037 11.8607 8.1197 11.6262C8.12064 11.5403 8.14335 11.4561 8.18568 11.3814C8.22802 11.3068 8.28861 11.2441 8.36178 11.1992C8.43719 11.1582 8.52173 11.1371 8.60753 11.1377C8.69332 11.1383 8.77756 11.1607 8.85237 11.2027C9.06003 11.3123 9.25837 11.4004 9.4777 11.429C9.89944 11.4846 10.326 11.3705 10.6636 11.1117C10.8094 10.9997 10.9203 10.862 11.0235 10.7086C11.1227 10.5604 11.2312 10.3732 11.3607 10.1486L11.3753 10.1229C11.5054 9.89833 11.6133 9.71108 11.6915 9.55124C11.7731 9.38441 11.8373 9.21933 11.8606 9.03791C11.9163 8.61616 11.8021 8.18959 11.5433 7.85199C11.4085 7.67641 11.233 7.54866 11.0346 7.42383C10.9607 7.38003 10.8992 7.3182 10.8557 7.24413C10.8123 7.17006 10.7883 7.08618 10.7861 7.00033C10.7861 6.83816 10.8748 6.67716 11.0346 6.57683C11.233 6.45199 11.408 6.32424 11.5433 6.14866C11.6715 5.98154 11.7656 5.7908 11.8202 5.58732C11.8747 5.38384 11.8887 5.17161 11.8612 4.96274C11.8337 4.78342 11.7765 4.60994 11.692 4.44941C11.5924 4.25561 11.4868 4.06497 11.3753 3.87774L11.3607 3.85208C11.2542 3.66196 11.1417 3.4752 11.0235 3.29208C10.9267 3.13887 10.8051 3.00285 10.6636 2.88958C10.4965 2.76121 10.3058 2.66701 10.1023 2.61236C9.89886 2.55771 9.6866 2.54368 9.4777 2.57108C9.25837 2.60024 9.06003 2.68774 8.85237 2.79799C8.77758 2.8398 8.69342 2.862 8.60774 2.86251C8.52206 2.86302 8.43765 2.84183 8.36237 2.80091C8.28906 2.75621 8.22829 2.69365 8.18575 2.61907C8.14321 2.54449 8.12028 2.46034 8.11912 2.37449C8.11037 2.13999 8.08762 1.92416 8.00245 1.71999C7.9219 1.5253 7.80378 1.34839 7.65485 1.19936C7.50592 1.05033 7.32908 0.932092 7.13445 0.851409C6.96528 0.781409 6.79028 0.753992 6.60537 0.741742C6.42803 0.729492 6.21162 0.729492 5.95203 0.729492H5.92287ZM5.07528 1.65991C5.1202 1.64124 5.18845 1.62433 5.32903 1.61441C5.47312 1.60449 5.65978 1.60449 5.93745 1.60449C6.21512 1.60449 6.40178 1.60449 6.54587 1.61441C6.68645 1.62433 6.7547 1.64124 6.79962 1.65991C6.9787 1.73399 7.12045 1.87574 7.19453 2.05483C7.21787 2.11083 7.23712 2.19891 7.2447 2.40716C7.2622 2.86916 7.50078 3.31424 7.92428 3.55866C8.34778 3.80308 8.85237 3.78733 9.26128 3.57149C9.44562 3.47408 9.53137 3.44666 9.59203 3.43908C9.7837 3.41375 9.97757 3.46558 10.131 3.58316C10.1695 3.61291 10.2185 3.66366 10.2973 3.78033C10.3784 3.90049 10.4717 4.06208 10.6105 4.30241C10.7494 4.54274 10.8421 4.70491 10.9057 4.83441C10.9681 4.96099 10.9874 5.02866 10.9932 5.07708C11.0185 5.26874 10.9667 5.46261 10.8491 5.61608C10.8118 5.66449 10.7453 5.72516 10.5691 5.83599C10.1771 6.08216 9.91112 6.51149 9.91112 7.00033C9.91112 7.48916 10.1771 7.91849 10.5691 8.16466C10.7453 8.27549 10.8118 8.33616 10.8491 8.38458C10.9669 8.53799 11.0183 8.73166 10.9932 8.92358C10.9874 8.97199 10.9675 9.04024 10.9057 9.16624C10.8421 9.29633 10.7494 9.45791 10.6105 9.69824C10.4717 9.93858 10.3778 10.1002 10.2973 10.2203C10.2185 10.337 10.1695 10.3877 10.131 10.4175C9.97757 10.5351 9.7837 10.5869 9.59203 10.5616C9.53137 10.554 9.4462 10.5266 9.26128 10.4292C8.85295 10.2133 8.34778 10.197 7.92428 10.4414C7.50078 10.6858 7.2622 11.1315 7.2447 11.5935C7.23712 11.8017 7.21787 11.8898 7.19453 11.9458C7.15789 12.0344 7.10415 12.1148 7.03638 12.1826C6.96862 12.2504 6.88816 12.3041 6.79962 12.3407C6.7547 12.3594 6.68645 12.3763 6.54587 12.3862C6.40178 12.3962 6.21512 12.3962 5.93745 12.3962C5.65978 12.3962 5.47312 12.3962 5.32903 12.3862C5.18845 12.3763 5.1202 12.3594 5.07528 12.3407C4.98674 12.3041 4.90628 12.2504 4.83852 12.1826C4.77075 12.1148 4.71701 12.0344 4.68037 11.9458C4.65703 11.8898 4.63778 11.8017 4.6302 11.5935C4.6127 11.1315 4.37412 10.6864 3.95062 10.442C3.52712 10.1976 3.02253 10.2133 2.61362 10.4292C2.42928 10.5266 2.34353 10.554 2.28287 10.5616C2.0912 10.5869 1.89733 10.5351 1.74387 10.4175C1.70537 10.3877 1.65637 10.337 1.57762 10.2203C1.46788 10.0495 1.36342 9.87544 1.26437 9.69824C1.12553 9.45791 1.03278 9.29574 0.9692 9.16624C0.906783 9.03966 0.887533 8.97199 0.8817 8.92358C0.856374 8.73191 0.9082 8.53804 1.02578 8.38458C1.06312 8.33616 1.12962 8.27549 1.30578 8.16466C1.69778 7.91849 1.96378 7.48916 1.96378 7.00033C1.96378 6.51149 1.69778 6.08216 1.30578 5.83599C1.12962 5.72516 1.06312 5.66449 1.02578 5.61608C0.9082 5.46261 0.856374 5.26874 0.8817 5.07708C0.887533 5.02866 0.907367 4.96041 0.9692 4.83441C1.03278 4.70433 1.12553 4.54274 1.26437 4.30241C1.4032 4.06208 1.49712 3.90049 1.57762 3.78033C1.65637 3.66366 1.70537 3.61291 1.74387 3.58316C1.89733 3.46558 2.0912 3.41375 2.28287 3.43908C2.34353 3.44666 2.4287 3.47408 2.61362 3.57149C3.02195 3.78733 3.52712 3.80366 3.95062 3.55866C4.37412 3.31424 4.6127 2.86916 4.6302 2.40716C4.63778 2.19891 4.65703 2.11083 4.68037 2.05483C4.75445 1.87574 4.8962 1.73399 5.07528 1.65991Z" fill="var(--text)" />
    </svg>

);

export const SubscriptionIcon = () => (
    <svg viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.74994 6.75H8.24994M1.24994 3.6875H12.6249M9.99994 6.75H10.8749M2.12494 1.0625C1.68744 1.0625 1.24994 1.5 1.24994 1.9375V8.0625C1.24994 8.5 1.68744 8.9375 2.12494 8.9375H11.7499C12.1874 8.9375 12.6249 8.5 12.6249 8.0625V1.9375C12.6249 1.5 12.1874 1.0625 11.7499 1.0625H2.12494Z" stroke="var(--text)" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

);

export const SignOutIcon = () => (
    <svg viewBox="-3 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 5.73529C0 5.8874 0.0581013 6.03328 0.161522 6.14084C0.264943 6.2484 0.405211 6.30882 0.551471 6.30882H4.73713L3.46875 7.62221C3.41706 7.67552 3.37604 7.73896 3.34804 7.80885C3.32004 7.87874 3.30563 7.9537 3.30563 8.02941C3.30563 8.10512 3.32004 8.18009 3.34804 8.24998C3.37604 8.31987 3.41706 8.3833 3.46875 8.43662C3.52002 8.49037 3.58101 8.53304 3.64821 8.56216C3.71541 8.59128 3.78749 8.60627 3.86029 8.60627C3.93309 8.60627 4.00518 8.59128 4.07238 8.56216C4.13958 8.53304 4.20057 8.49037 4.25184 8.43662L6.45772 6.1425C6.50793 6.08796 6.54728 6.02364 6.57353 5.95324C6.62869 5.8136 6.62869 5.65699 6.57353 5.51735C6.54728 5.44695 6.50793 5.38263 6.45772 5.32809L4.25184 3.03397C4.20042 2.9805 4.13938 2.93808 4.0722 2.90914C4.00502 2.8802 3.93301 2.8653 3.86029 2.8653C3.78758 2.8653 3.71557 2.8802 3.64839 2.90914C3.58121 2.93808 3.52017 2.9805 3.46875 3.03397C3.41733 3.08745 3.37654 3.15093 3.34872 3.2208C3.32089 3.29067 3.30657 3.36555 3.30657 3.44118C3.30657 3.5168 3.32089 3.59169 3.34872 3.66156C3.37654 3.73142 3.41733 3.79491 3.46875 3.84838L4.73713 5.16176H0.551471C0.405211 5.16176 0.264943 5.22219 0.161522 5.32975C0.0581013 5.43731 0 5.58318 0 5.73529ZM7.16912 0H1.65441C1.21563 0 0.794828 0.181276 0.484566 0.503949C0.174304 0.826622 0 1.26426 0 1.72059V3.44118C0 3.59329 0.0581013 3.73917 0.161522 3.84672C0.264943 3.95428 0.405211 4.01471 0.551471 4.01471C0.69773 4.01471 0.837998 3.95428 0.941419 3.84672C1.04484 3.73917 1.10294 3.59329 1.10294 3.44118V1.72059C1.10294 1.56848 1.16104 1.4226 1.26446 1.31504C1.36788 1.20748 1.50815 1.14706 1.65441 1.14706H7.16912C7.31538 1.14706 7.45565 1.20748 7.55907 1.31504C7.66249 1.4226 7.72059 1.56848 7.72059 1.72059V9.75C7.72059 9.90211 7.66249 10.048 7.55907 10.1555C7.45565 10.2631 7.31538 10.3235 7.16912 10.3235H1.65441C1.50815 10.3235 1.36788 10.2631 1.26446 10.1555C1.16104 10.048 1.10294 9.90211 1.10294 9.75V8.02941C1.10294 7.8773 1.04484 7.73142 0.941419 7.62387C0.837998 7.51631 0.69773 7.45588 0.551471 7.45588C0.405211 7.45588 0.264943 7.51631 0.161522 7.62387C0.0581013 7.73142 0 7.8773 0 8.02941V9.75C0 10.2063 0.174304 10.644 0.484566 10.9666C0.794828 11.2893 1.21563 11.4706 1.65441 11.4706H7.16912C7.60789 11.4706 8.0287 11.2893 8.33896 10.9666C8.64923 10.644 8.82353 10.2063 8.82353 9.75V1.72059C8.82353 1.26426 8.64923 0.826622 8.33896 0.503949C8.0287 0.181276 7.60789 0 7.16912 0Z" fill="var(--text)" />
    </svg>
);

export default Avatar;